import type { PlayerState } from "./types";

/**
 * Motor de logros/badges declarativo. Un badge se define con un PREDICADO sobre
 * el estado del jugador. Se evalúan tras cada avance; los recién cumplidos se
 * otorgan una sola vez. Predicados componibles → no hay lógica hard-coded.
 */

export type BadgePredicate = (state: PlayerState) => boolean;

export interface BadgeDef {
  id: string;
  /** i18n key o texto; el core no renderiza. */
  title: string;
  description?: string;
  when: BadgePredicate;
  /** XP de bonificación al obtenerlo (opcional). */
  bonusXp?: number;
}

// ── Predicados componibles ────────────────────────────────────────────────

/** El contador `key` alcanza `n`. */
export function counterAtLeast(key: string, n: number): BadgePredicate {
  return (s) => (s.counters[key] ?? 0) >= n;
}

/** La racha de la línea `line` alcanza `n`. */
export function streakAtLeast(line: string, n: number): BadgePredicate {
  return (s) =>
    (s.streaks[line]?.best ?? 0) >= n || (s.streaks[line]?.current ?? 0) >= n;
}

/** El nivel alcanza `n`. */
export function levelAtLeast(n: number): BadgePredicate {
  return (s) => s.level >= n;
}

export function allOf(...preds: BadgePredicate[]): BadgePredicate {
  return (s) => preds.every((p) => p(s));
}

export function anyOf(...preds: BadgePredicate[]): BadgePredicate {
  return (s) => preds.some((p) => p(s));
}

/**
 * Devuelve los badges recién obtenidos (que se cumplen ahora y no estaban ya
 * en `state.badges`). NO muta el estado.
 */
export function newlyEarnedBadges(
  state: PlayerState,
  defs: readonly BadgeDef[],
): BadgeDef[] {
  const owned = new Set(state.badges);
  const earned: BadgeDef[] = [];
  for (const def of defs) {
    if (!owned.has(def.id) && def.when(state)) {
      earned.push(def);
    }
  }
  return earned;
}
